import { useMemo } from 'react';
import { useMutation, useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { Pagination, PaginationParams } from '@/common/types';
import { API_URL, IClaimRepaymentAggregatedDto } from '@/dtos';
import { RepaymentMatching } from '@/modules/employer/components/Lockbox/lockbox.types';

interface RepaymentMatchingPayload {
  employee_name: string;
  claim_id: number;
  employee_id: number;
  organization_id: number;
  organization_path: string;
  claim_amount: number;
  available_for_repayment_amount: number;
}

export const useGetClaimsRepaymentMatchesQueryAsync = () => {
  const { mutateAsync, isLoading } = useMutation(
    async ({
      searchString,
      page = 0,
      perPage = 50,
    }: {
      searchString: string;
      page?: number;
      perPage?: number;
    }): Promise<RepaymentMatching[]> => {
      const params = {
        search_value: searchString,
        page,
        size: perPage,
      };

      const response = await api.get<Pagination<IClaimRepaymentAggregatedDto>>(
        API_URL.CLAIMS_REPAYMENT_MATCHES,
        {
          params,
        }
      );

      const content = response?.data?.content || [];

      return content.map(
        (claimsRepaymentMatching): RepaymentMatching => ({
          employeeName: claimsRepaymentMatching?.employee_name || '',
          claimId: toString(claimsRepaymentMatching?.claim_id),
          employeeId: toString(claimsRepaymentMatching?.employee_id),
          organizationId: toString(claimsRepaymentMatching?.organization_id),
          organizationPath: claimsRepaymentMatching?.organization_path || '',
          claimAmount: claimsRepaymentMatching?.claim_amount || 0,
          availableForRepaymentAmount: claimsRepaymentMatching?.available_for_repayment_amount || 0,
        })
      );
    }
  );

  return {
    getClaimsMatches: mutateAsync,
    isLoading,
  };
};

interface Params extends PaginationParams {}
export const useGetClaimsRepaymentMatchesQuery = ({ searchString, page, perPage }: Params) => {
  const params = {
    search_value: searchString,
    page,
    size: perPage,
  };

  const { data, isLoading } = useQuery(
    [API_URL.CLAIMS_REPAYMENT_MATCHES, searchString, page, perPage],
    () => api.get<Pagination<RepaymentMatchingPayload>>(API_URL.CLAIMS_REPAYMENT_MATCHES, { params }),
    { enabled: !!searchString }
  );

  const claimsRepaymentMatches = useMemo(() => {
    if (!data?.data?.content?.length) {
      return [];
    }

    return data?.data?.content?.map((claimsRepaymentMatching) => ({
      employeeName: claimsRepaymentMatching?.employee_name,
      claimId: toString(claimsRepaymentMatching?.claim_id),
      employeeId: toString(claimsRepaymentMatching?.employee_id),
      organizationId: toString(claimsRepaymentMatching?.organization_id),
      organizationPath: claimsRepaymentMatching?.organization_path,
      claimAmount: claimsRepaymentMatching?.claim_amount,
      availableForRepaymentAmount: claimsRepaymentMatching?.available_for_repayment_amount,
    }));
  }, [data]);

  return {
    claimsRepaymentMatches,
    isLoading,
  };
};
